import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

export default function Auth() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { login, register } = useAuth();
    const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
    const [role, setRole] = useState("writer");
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [privacyOk, setPrivacyOk] = useState(false);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (mode === "register" && !privacyOk) {
            toast.error("Devi accettare l'informativa privacy per registrarti");
            return;
        }
        setLoading(true);
        try {
            if (mode === "login") {
                await login(form.email, form.password);
                toast.success("Bentornato!");
            } else {
                await register({ name: form.name, email: form.email, password: form.password, role, privacy_accepted: true });
                toast.success("Registrazione completata!");
            }
            navigate(role === "editor" && mode === "register" ? "/dashboard" : "/editors");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setLoading(false); }
    };

    const googleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
            <div className="hidden lg:flex flex-col justify-between bg-paper border-r border-border p-12">
                <Link to="/" className="flex items-center gap-2">
                    <BookOpen className="text-primary" size={24} /><span className="font-serif text-3xl font-semibold">Incipit</span>
                </Link>
                <div>
                    <h2 className="text-4xl font-serif font-medium leading-tight">L'editoria libera<br />inizia da qui.</h2>
                    <p className="text-muted-foreground mt-4 max-w-sm">Unisciti alla comunità di scrittori ed editor. Nessun costo, nessuna commissione.</p>
                </div>
                <p className="flex items-center gap-2 text-sm text-primary font-medium"><Heart size={16} /> 100% gratuito per sempre</p>
            </div>

            <div className="flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-md">
                    <div className="flex gap-1 p-1 bg-secondary rounded-lg mb-8">
                        {["login", "register"].map((m) => (
                            <button key={m} onClick={() => setMode(m)} data-testid={`tab-${m}`}
                                className={`flex-1 py-2.5 rounded-md text-sm font-medium transition-colors ${mode === m ? "bg-card shadow-sm" : "text-muted-foreground"}`}>
                                {m === "login" ? "Accedi" : "Registrati"}
                            </button>
                        ))}
                    </div>

                    <h1 className="text-3xl font-serif font-medium mb-1">{mode === "login" ? "Bentornato" : "Crea il tuo account"}</h1>
                    <p className="text-muted-foreground mb-6 text-sm">{mode === "login" ? "Accedi per continuare" : "Gratuito per scrittori ed editor"}</p>

                    <Button onClick={googleLogin} variant="outline" className="w-full gap-2 mb-4 h-11" data-testid="google-login-btn">
                        <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="w-5 h-5" /> Continua con Google
                    </Button>
                    <div className="flex items-center gap-3 my-4 text-xs text-muted-foreground">
                        <div className="h-px bg-border flex-1" /> oppure <div className="h-px bg-border flex-1" />
                    </div>

                    <form onSubmit={submit} className="space-y-4">
                        {mode === "register" && (
                            <>
                                <div>
                                    <Label>Sono un…</Label>
                                    <div className="grid grid-cols-2 gap-3 mt-2">
                                        {[{ v: "writer", l: "Scrittore" }, { v: "editor", l: "Editor freelance" }].map((r) => (
                                            <button type="button" key={r.v} onClick={() => setRole(r.v)} data-testid={`role-${r.v}`}
                                                className={`py-3 rounded-lg border text-sm font-medium transition-colors ${role === r.v ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>
                                                {r.l}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" data-testid="input-name" /></div>
                            </>
                        )}
                        <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5" data-testid="input-email" /></div>
                        <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="mt-1.5" data-testid="input-password" /></div>
                        {mode === "register" && (
                            <label className="flex items-start gap-2.5 text-sm text-muted-foreground cursor-pointer">
                                <Checkbox checked={privacyOk} onCheckedChange={(v) => setPrivacyOk(!!v)} className="mt-0.5" data-testid="privacy-consent" />
                                <span>Ho letto e accetto l'<Link to="/privacy" target="_blank" className="text-primary underline">Informativa Privacy</Link> e autorizzo il trattamento dei miei dati personali.</span>
                            </label>
                        )}
                        <Button type="submit" disabled={loading} className="w-full h-11 bg-primary hover:bg-primary/90" data-testid="submit-auth">
                            {loading ? "Attendere…" : mode === "login" ? "Accedi" : "Registrati"}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}
